import { createEvent, createStore, restore, sample } from 'effector'
import { createGate } from 'effector-react'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'
import { fx } from 'src/libs'
import { env } from 'src/libs/constants'
import { BASE_URL, PAGE_SIZE } from 'src/pages/finSystem/debt/const'
import {
  Debt,
  DebtListResponse,
  DebtPayload,
  DeptPayloadEdit,
  ListRequest,
  OptionType,
  TotalsDebts,
  WalletResponse,
} from 'src/pages/finSystem/debt/types'

const initialValueTotals = {
  balanceDueUsd: 0,
  recoveredUsd: 0,
}

export const DebtListGate = createGate()

export const debtListLoad = createEvent<ListRequest>()

export const getDebtListFx = fx<ListRequest, DebtListResponse>({
  method: 'GET',
  url: BASE_URL,
})

export const getWalletsFx = fx<void, WalletResponse>({
  method: 'GET',
  url: `/finance/wallet`,
  query: { order: 'name' },
})

export const getWalletsWithArchiveFx = fx<void, WalletResponse>({
  method: 'GET',
  url: `/finance/wallet`,
  query: { where: JSON.stringify({ includeArchived: true }), order: 'name' },
})

export const submitCreateDebtFx = fx<DebtPayload, DebtPayload>({
  method: 'POST',
  url: BASE_URL,
})

export const submitEditDebtFx = fx<Partial<DeptPayloadEdit>, DebtPayload>({
  method: 'PUT',
})

export const submitDeleteDebtFx = fx<{ id: number }, void>({
  method: 'DELETE',
})

export const getExchangeRateFx = fx<
  {
    baseCurrency: string
    quoteCurrency: string
    amount: string
  },
  {
    symbol: string
    rate: string
  }
>({
  method: 'POST',
  baseUrl: env.baseUrl && env.baseUrl.replace('/v1', ''),
  url: '/convert',
})

export const $debts = createStore<Debt[]>([]).on(
  getDebtListFx.doneData,
  (_, res) => res.body.items,
)

export const $totalItems = createStore<number>(0).on(
  getDebtListFx.doneData,
  (_, res) => res.body.total,
)

export const $totalsDebts = createStore<TotalsDebts>(initialValueTotals).on(
  getDebtListFx.doneData,
  (_, res) => res.body.totals,
)

export const $optionsWallet = restore<
  (OptionType & {
    localBalances: {
      currency: string
      sum: number
      sumUSD: number
    }[]
  })[]
>(
  getWalletsFx.doneData.map((a) =>
    a.body.items.map(({ name, id, localBalances }) => ({
      label: name,
      value: id,
      localBalances,
    })),
  ),
  [],
)

export const $optionsWalletWithArchive = restore<OptionType[]>(
  getWalletsWithArchiveFx.doneData.map((a) => {
    return a.body.items.map(({ name, id }) => ({ label: name, value: id }))
  }),
  [],
)

sample({
  clock: DebtListGate.open,
  fn: () => ({}),
  target: [getWalletsWithArchiveFx, getWalletsFx],
})

sample({
  clock: debtListLoad,
  fn: ({
    id,
    walletIds,
    dateFrom,
    dateTo,
    userId,
    statuses,
    sorterField = '-dateTime',
    page = 1,
    pageSize = PAGE_SIZE,
  }) => {
    const query: Record<string, any> = {
      page,
      limit: pageSize,
      order: sorterField,
    }
    const where = Object.fromEntries(
      Object.entries({
        id,
        walletIds,
        dateFrom,
        dateTo,
        userId,
        statuses,
      }).filter(([, value]) => Boolean(value)),
    )

    if (Object.keys(where).length > 0)
      query['where'] = JSON.stringify({ ...where })
    return { query }
  },
  target: getDebtListFx,
})

sample({
  clock: [
    getDebtListFx.failData,
    submitCreateDebtFx.failData,
    getWalletsWithArchiveFx.failData,
    getWalletsFx.failData,
    getExchangeRateFx.failData,
    submitDeleteDebtFx.failData,
    submitEditDebtFx.failData,
  ],
  fn: (error): NotificationPayload => ({
    type: 'error',
    description: error?.body?.message ?? 'Request failed',
  }),
  target: showNotification,
})

sample({
  clock: submitCreateDebtFx.done,
  fn: (): NotificationPayload => ({
    type: 'success',
    description: 'Debt transaction created successfully',
  }),
  target: showNotification,
})

sample({
  clock: submitDeleteDebtFx.done,
  fn: (): NotificationPayload => ({
    type: 'success',
    description: 'Debt transaction deleted successfully',
  }),
  target: showNotification,
})

sample({
  clock: submitEditDebtFx.done,
  fn: (): NotificationPayload => ({
    type: 'success',
    description: 'Debt transaction updated successfully',
  }),
  target: showNotification,
})
